import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { ASSETS } from "../data/menu";

const links = [
  { label: "About", href: "#about" },
  { label: "Categories", href: "#categories" },
  { label: "Menu", href: "#menu" },
  { label: "Signature", href: "#signature" },
  { label: "Gallery", href: "#gallery" },
  { label: "Visit", href: "#visit" },
];

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="site-navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 bg-cream ${
        scrolled ? "border-b border-espresso/10" : ""
      }`}
    >
      <nav className="max-w-[1400px] mx-auto px-6 md:px-10 h-24 flex items-center justify-between">
        <a
          href="#top"
          data-testid="nav-logo-link"
          className="flex items-center"
          aria-label="OROMA — home"
        >
          <div className="relative h-24 md:h-28 w-64 md:w-80 overflow-hidden flex items-center justify-start">
            <img
              src={ASSETS.logo}
              alt="OROMA — your daily ritual"
              className="w-auto h-[240%] object-contain -ml-6 md:-ml-10"
              style={{ mixBlendMode: "darken" }}
              data-testid="nav-logo-image"
            />
          </div>
        </a>

        <ul className="hidden lg:flex items-center gap-9">
          {links.map((l) => (
            <li key={l.href}>
              <a
                href={l.href}
                data-testid={`nav-link-${l.label.toLowerCase()}`}
                className="link-underline text-[13px] tracking-[0.22em] uppercase text-espresso/80 hover:text-espresso font-medium"
              >
                {l.label}
              </a>
            </li>
          ))}
        </ul>

        <a
          href="#menu"
          data-testid="nav-cta-view-menu"
          className="hidden lg:inline-flex items-center px-5 py-2.5 text-[12px] tracking-[0.25em] uppercase border border-espresso text-espresso hover:bg-espresso hover:text-cream transition-colors duration-500"
        >
          View Menu
        </a>

        <button
          className="lg:hidden text-espresso p-2"
          onClick={() => setOpen((v) => !v)}
          aria-label="Toggle menu"
          data-testid="mobile-menu-toggle"
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </nav>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.35 }}
            className="lg:hidden overflow-hidden bg-cream/95 backdrop-blur-md border-t border-espresso/10"
            data-testid="mobile-menu-panel"
          >
            <ul className="flex flex-col px-8 py-6 gap-5">
              {links.map((l) => (
                <li key={l.href}>
                  <a
                    href={l.href}
                    data-testid={`mobile-nav-link-${l.label.toLowerCase()}`}
                    onClick={() => setOpen(false)}
                    className="font-serif text-2xl text-espresso"
                  >
                    {l.label}
                  </a>
                </li>
              ))}
              <li>
                <a
                  href="#menu"
                  onClick={() => setOpen(false)}
                  data-testid="mobile-nav-cta"
                  className="inline-flex items-center px-5 py-2.5 text-[12px] tracking-[0.25em] uppercase border border-espresso text-espresso"
                >
                  View Menu
                </a>
              </li>
            </ul>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};
